import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { PageTitle } from '@/components/page-title';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Modal } from '@/components/modal';
import { Skeleton } from '@/components/ui/skeleton';
import { useNotification } from '@/components/notifications';
import { Plus, Trash2, Globe, AlertCircle } from 'lucide-react';
import { OrganizationUrlItem } from '@/types/composer-dashboard';
import {
  getPostHogUrls,
  createPostHogUrl,
  deletePostHogUrl,
} from '@/services/admin/posthog-urls';
import { useOrgStore } from '@/stores/org-store';

export function AnalyticsConfigPage() {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const orgName = currentOrg?.name || orgId || '';

  const [urls, setUrls] = useState<OrganizationUrlItem[]>([]);
  const [isUrlsLoading, setIsUrlsLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newUrlPattern, setNewUrlPattern] = useState('');
  const [newUrlLabel, setNewUrlLabel] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notification = useNotification();
  const tableRef = useRef<HTMLDivElement>(null);

  const fetchUrls = useCallback(async () => {
    if (!orgId) return;

    setIsUrlsLoading(true);
    try {
      const response = await getPostHogUrls(orgId);
      setUrls(response.urls);
    } catch (error) {
      console.error('Failed to fetch URLs:', error);
      Sentry.captureException(error, {
        extra: {
          context: 'fetch-posthog-urls',
          orgId,
        },
      });
      notification.error('Failed to load PostHog URLs');
    } finally {
      setIsUrlsLoading(false);
    }
  }, [orgId, notification]);

  useEffect(() => {
    if (orgId) {
      fetchUrls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleAddUrl = async () => {
    if (!orgId || !newUrlPattern.trim()) {
      notification.error('URL pattern is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createPostHogUrl(orgId, {
        url_pattern: newUrlPattern.trim(),
        label: newUrlLabel.trim() || null,
      });
      setUrls((prev) => [response.url, ...prev]);
      setNewUrlPattern('');
      setNewUrlLabel('');
      setIsAddDialogOpen(false);
      notification.success('URL pattern added successfully');
    } catch (error) {
      console.error('Failed to add URL:', error);
      Sentry.captureException(error, {
        extra: {
          context: 'create-posthog-url',
          orgId,
          urlPattern: newUrlPattern,
        },
      });
      notification.error('Failed to add URL pattern');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUrl = async (urlId: string) => {
    if (!orgId) return;
    try {
      await deletePostHogUrl(orgId, urlId);
      setUrls((prev) => prev.filter((u) => u.id !== urlId));
      notification.success('URL pattern deleted');
    } catch (error) {
      console.error('Failed to delete URL:', error);
      Sentry.captureException(error, {
        extra: {
          context: 'delete-posthog-url',
          orgId,
          urlId,
        },
      });
      notification.error('Failed to delete URL pattern');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!orgId) {
    return (
      <div className='w-full space-y-6'>
        <PageTitle title='Analytics Configuration' />
        <p className='text-muted-foreground'>No organization selected.</p>
      </div>
    );
  }

  return (
    <div className='w-full space-y-6'>
      <div className='space-y-2'>
        <PageTitle title='Analytics Configuration' />
        <p className='text-base text-muted-foreground'>
          Configure PostHog URL patterns for <strong>{orgName}</strong>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Globe className='size-5' />
            PostHog URL Patterns
          </CardTitle>
          <CardDescription>
            Define URL patterns to filter PostHog analytics data. Use SQL LIKE
            patterns (e.g., https://example.com%). Switch organizations using
            the org switcher in the sidebar.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center justify-between'>
            <div className='text-sm text-muted-foreground'>
              Organization:{' '}
              <span className='font-medium text-foreground'>{orgName}</span>
              <span className='ml-2 font-mono text-xs text-muted-foreground'>
                ({orgId})
              </span>
            </div>

            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className='size-4 mr-2' />
              Add URL Pattern
            </Button>

            <Modal
              isDialogOpen={isAddDialogOpen}
              setIsDialogOpen={setIsAddDialogOpen}
              title='Add PostHog URL Pattern'
              description='Add a new URL pattern to filter PostHog analytics for this organization.'
              footer={
                <>
                  <Button
                    variant='outline'
                    onClick={() => setIsAddDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddUrl} disabled={isSubmitting}>
                    {isSubmitting ? 'Adding...' : 'Add URL Pattern'}
                  </Button>
                </>
              }
            >
              <div className='space-y-4 py-4'>
                <div className='space-y-2'>
                  <Label htmlFor='url-pattern'>URL Pattern *</Label>
                  <Input
                    id='url-pattern'
                    placeholder='https://example.com%'
                    value={newUrlPattern}
                    onChange={(e) => setNewUrlPattern(e.target.value)}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Use % as a wildcard. Example: https://app.example.com%
                    matches all pages on that domain.
                  </p>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='url-label'>Label (optional)</Label>
                  <Input
                    id='url-label'
                    placeholder='Main App'
                    value={newUrlLabel}
                    onChange={(e) => setNewUrlLabel(e.target.value)}
                  />
                </div>
              </div>
            </Modal>
          </div>

          {/* URLs Table */}
          {isUrlsLoading ? (
            <Skeleton className='h-[200px]' />
          ) : urls.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <AlertCircle className='size-12 text-muted-foreground mb-4' />
              <h3 className='font-semibold text-lg'>
                No URL patterns configured
              </h3>
              <p className='text-sm text-muted-foreground mt-1'>
                Add URL patterns to enable web analytics for this organization.
              </p>
            </div>
          ) : (
            <div className='rounded-md border'>
              <Table tableRef={tableRef}>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL Pattern</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className='w-[80px]'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {urls.map((url) => (
                    <TableRow key={url.id}>
                      <TableCell className='font-mono text-sm'>
                        {url.url_pattern}
                      </TableCell>
                      <TableCell>{url.label || '—'}</TableCell>
                      <TableCell>{formatDate(url.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => handleDeleteUrl(url.id)}
                          className='text-destructive hover:text-destructive'
                        >
                          <Trash2 className='size-4' />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
